// HEAL-46b D-30 (§9.1) — своё окно перед системным.
//
// Системное окно показывается один раз за установку, и потратить его на
// автоматический запрос при запуске — самый дешёвый способ получить отказ
// навсегда: на iOS повторно спросить нельзя, только отправить в настройки.
//
// Поэтому сначала спрашиваем сами, и только по нажатию «Сообщить» зовём систему.
// Своё окно можно показать ещё раз через неделю, системное — никогда.
//
// Момент выбран так, чтобы польза была очевидна из контекста: файлы приняты и
// обрабатываются, человек как раз решает, ждать ему или уйти.

import { useEffect, useState } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { enablePush, mayAskForPush, rememberAsked } from '@/lib/push';

type Props = {
  /** Показывать только пока есть чего ждать: всё обработано — предлагать поздно. */
  active: boolean;
};

export function PushPermissionPrompt({ active }: Props) {
  const [eligible, setEligible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!active) return;
    let alive = true;
    void mayAskForPush().then((may) => {
      if (alive) setEligible(may);
    });
    return () => {
      alive = false;
    };
  }, [active]);

  if (!active || !eligible) return null;

  const allow = async () => {
    setBusy(true);
    // Исход не показываем: разрешил — уведомление придёт и само всё скажет,
    // отказал — приложение работает ровно как раньше (I-15). Плашка «вы
    // отказались» была бы упрёком за законный выбор.
    await enablePush();
    setEligible(false);
  };

  const decline = async () => {
    await rememberAsked();
    setEligible(false);
  };

  return (
    <div className="mb-3 rounded-surface border border-border bg-muted px-4 py-3">
      <div className="flex items-start gap-3">
        <Bell className="mt-0.5 h-5 w-5 shrink-0 text-primary-text" />
        <div className="flex-1">
          <p className="text-sm font-medium leading-snug">Сообщить, когда анализы будут готовы?</p>
          <p className="mt-0.5 text-sm text-muted-foreground">Не придётся держать приложение открытым.</p>
          <div className="mt-3 flex gap-2">
            <Button size="sm" className="flex-1" disabled={busy} onClick={() => void allow()}>
              Сообщить
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="flex-1 text-muted-foreground"
              disabled={busy}
              onClick={() => void decline()}
            >
              Не нужно
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
